import React, { useState, useRef, useEffect, useCallback } from "react";
import { 
  FaSearch, 
  FaTimes, 
  FaUserCircle, 
  FaSignOutAlt, 
  FaPlus, 
  FaChevronDown,
  FaBell,
  FaCalendar,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "./CardsTopBar.css";
import NotificationsPanel from "./NotificationsPanel";
import UserProfileCard from "../components/UserProfileCard";
import Agenda from "./Agenda";

export default function CardsTopBar({
  projectName,
  onSearch,
  profile,
  session,
  onLogout,
  onProfileUpdate,
  onAddColumn,
  onAddDiario,
  onAddCompartilhada, // NOVO
  jaExisteDiario,
  modoPlanejamento = false,
  containerAtual,
  onOpenAgendaItem,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  const [showAgenda, setShowAgenda] = useState(false);

  const addMenuRef = useRef(null);
  const notifPanelRef = useRef(null);

  // Auto-open profile card if incomplete
  useEffect(() => {
    if (profile && (!profile.nome || !profile.empresa || !profile.funcao || !profile.container)) {
      setShowProfileCard(true);
    }
  }, [profile]);

  useEffect(() => {
    const fn = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) setShowNotifPanel(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!session?.user?.id || !session?.user?.email) return;
    try {
      const [{ count: convitesCount }, { count: mencoesCount }] = await Promise.all([
        supabase.from("convites")
          .select("id", { count: "exact", head: true })
          .eq("email", session.user.email)
          .eq("status", "pendente"),
        supabase.from("notificacoes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", session.user.id)
          .eq("lido", false),
      ]);
      setNotificacoesNaoLidas((convitesCount || 0) + (mencoesCount || 0));
    } catch (err) {
      console.error("Erro ao contar notificações:", err);
    }
  }, [session?.user?.id, session?.user?.email]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!session?.user?.id) return;
    const channel = supabase
      .channel(`notificacoes-badge-cards-${session.user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notificacoes", filter: `user_id=eq.${session.user.id}` },
        () => fetchUnreadCount()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, fetchUnreadCount]);

  const avatarUrl = profile?.avatar_url || null;
  const firstName = profile?.nome ? profile.nome.split(" ")[0] : "Usuário";

  const handleSearch = (e) => { 
    setSearchTerm(e.target.value); 
    onSearch?.(e.target.value); 
  };
  
  const clearSearch = () => { 
    setSearchTerm(""); 
    onSearch?.(""); 
  };
  
  const handleLogout = async () => { 
    await supabase.auth.signOut(); 
    onLogout?.(); 
  };
  
  const handleAvatarClick = () => setShowProfileCard(true);

  return (
    <>
      <header className={`ctb ${modoPlanejamento ? 'ctb--planejamento' : ''}`}>
        <div className="ctb__title">{projectName || "Projeto"}</div>

        <div className="ctb__spacer" />

        <div className="ctb__search">
          <FaSearch size={12} className="ctb__search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search"
            className="ctb__search-input"
          />
          {searchTerm && (
            <button className="ctb__search-clear" onClick={clearSearch}>
              <FaTimes size={10} />
            </button>
          )}
        </div>

        <div className="ctb__add-wrap" ref={addMenuRef}>
          <button className="ctb__add-btn" onClick={() => setShowAddMenu(p => !p)}>
            <FaPlus size={11} />
            <span>Pilha</span>
            <FaChevronDown size={9} className="ctb__add-chevron" />
          </button>
          {showAddMenu && (
            <div className="ctb__add-menu">
              <button className="ctb__add-menu-item" onClick={() => { onAddColumn?.(); setShowAddMenu(false); }}>
                Pilha normal
              </button>
              {!jaExisteDiario && (
                <button className="ctb__add-menu-item" onClick={() => { onAddDiario?.(); setShowAddMenu(false); }}>
                  Diário de Obra
                </button>
              )}
              <button className="ctb__add-menu-item" onClick={() => { onAddCompartilhada?.(); setShowAddMenu(false); }}>
                Pilha Compartilhada
              </button>
            </div>
          )}
        </div>

        <button className="ctb__bell" title="Agenda" onClick={() => setShowAgenda(true)}>
          <FaCalendar size={14} />
        </button>

        <div className="ctb__bell-wrap" ref={notifPanelRef}>
          <button
            className="ctb__bell"
            title="Notificações"
            onClick={() => setShowNotifPanel((v) => !v)}
          >
            <FaBell size={15} />
            {notificacoesNaoLidas > 0 && (
              <span className="ctb__bell-badge">
                {notificacoesNaoLidas > 9 ? "9+" : notificacoesNaoLidas}
              </span>
            )}
          </button>

          {showNotifPanel && (
            <NotificationsPanel
              user={session?.user}
              containerAtual={null}
              setContainerAtual={() => {}}
              onClose={() => setShowNotifPanel(false)}
            />
          )}
        </div>

        <div className="ctb__user" onClick={handleAvatarClick} style={{ cursor: "pointer" }}>
          <div className="ctb__avatar-wrap">
            {avatarUrl
              ? <img src={avatarUrl} alt="Avatar" className="ctb__avatar" />
              : <FaUserCircle className="ctb__avatar-fallback" />
            }
          </div>
          <span className="ctb__user-name">{firstName}</span>
        </div>
      </header>

      {showAgenda && (
        <Agenda
          user={session?.user}
          currentContainerId={containerAtual}
          onClose={() => setShowAgenda(false)}
          onOpenItem={(item) => {
            setShowAgenda(false);
            onOpenAgendaItem?.(item);
          }}
        />
      )}

      {/* UserProfileCard - substitui o modal inline */}
      {showProfileCard && (
        <UserProfileCard
          userId={session?.user?.id}
          userEmail={session?.user?.email}
          profile={profile}
          onProfileUpdate={onProfileUpdate}
          onLogout={handleLogout}
          onClose={() => setShowProfileCard(false)}
        />
      )}
    </>
  );
}